import React from 'react';
import { ColumnDef, VisibilityState } from '@tanstack/react-table';
import { HiChevronDown, HiChevronRight } from 'react-icons/hi2';
import GeneralReportDatePicker from '../../../general/GeneralReportDatePicker';
import {
  addCommasToNumbersAndRound,
  percentageToTwoDecimalPlaces,
} from '../../../../utilities/numberFormatters';
import CalendarPopup from './components/Calendar.component';
import PageLinkButton from '../../../buttons/PageLinkButton';
import { formatDateForCheckingState } from '../../../../utilities/dateFormatters';
import ReportsLinkButton from '../../aifmd/overviewLegacy/table/components/ReportsLinkButton/reportsLinkButton.component';
import { GeneralOverviewTableData } from './OverviewPage';
import { isPrimitive } from '../../../../utilities/parserUtilities';
import { statusSort } from '../../../../utilities/customSorting';
import { specifyStatus } from '../../../../utilities/generalMappings';
import NotesComp from '../notes/Notes';
import { Status } from '../../../../types/redux/data/dataTypes';
import { mainColors } from '../../../../styling/theme';
import { OverviewColumnFields, DetailLine } from './overview-columns';
import type { ColumnAlignMeta } from '../../../tables/tanstack/tanstackUtils';
import { Note } from '../../../../sharedInterfaces/notes';

function getDatesFromSubFundRiskReportNames(reportNames: string[]): Date[] {
  if (!reportNames?.length) return [];
  const dates: Date[] = [];
  reportNames.forEach((reportName: string) => {
    const formattedDate = reportName.slice(-14).replace('.pdf', '');
    dates.push(new Date(formattedDate));
  });
  return dates.sort((a, b) => a.getTime() - b.getTime());
}

function getDatesFromSubFundRiskReportNamesAsStrings(
  reportNames: string[],
): string[] {
  if (!reportNames?.length) return [];
  return reportNames.map((reportName: string) =>
    reportName.slice(-14).replace('.pdf', ''),
  );
}

function findSubFundReportnameFromDate(
  date: Date | null,
  reportNames: string[],
): string | null {
  if (!date) return null;
  const dateString = formatDateForCheckingState(date);
  let foundReportName = '';
  reportNames.forEach((reportName: string) => {
    const formattedDate = reportName.slice(-14).replace('.pdf', '');
    if (dateString === formattedDate) {
      foundReportName = reportName;
    }
  });
  return foundReportName || null;
}

type OverviewColumnDef = ColumnDef<GeneralOverviewTableData, unknown> & {
  meta?: ColumnAlignMeta;
};

/**
 * Columns kept in the table for global search only. Hidden columns are still
 * searched, since TanStack filters over `getAllLeafColumns()`.
 */
export const overviewInitialColumnVisibility: VisibilityState = {
  derogationMessage: false,
};

const STATUS_COLUMN_MIN_WIDTH = '7rem';

export function getOverviewColumnsV2(
  clientName: string | null,
  fields: OverviewColumnFields[] | null,
  section: string,
  getNotesForFund?: (fundName: string, positionDate: string) => Note[],
  preFetchedNotesLoading?: boolean,
): OverviewColumnDef[] {
  if (!fields) return [];

  const expandColumn: OverviewColumnDef = {
    id: 'expand',
    header: '',
    enableSorting: false,
    meta: { align: 'center', width: '2.5rem' },
    cell: ({ row }) => (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          row.toggleExpanded();
        }}
        className="rounded p-1 hover:bg-gray-100 focus:outline-none"
        aria-label={row.getIsExpanded() ? 'Collapse row' : 'Expand row'}
      >
        {row.getIsExpanded() ? (
          <HiChevronDown className="h-4 w-4 text-gray-500" />
        ) : (
          <HiChevronRight className="h-4 w-4 text-gray-500" />
        )}
      </button>
    ),
  };

  const allColumns: Record<string, OverviewColumnDef> = {
    index: {
      accessorKey: 'index',
      header: '#',
      enableSorting: false,
      enableGlobalFilter: false,
      meta: { align: 'center', width: '3rem' },
      cell: ({ getValue }) => getValue() as number,
    },
    internalFundName: {
      accessorKey: 'internalFundName',
      header: 'Internal Fund Name',
      meta: { align: 'left', minWidth: '8rem' },
      cell: ({ row }) =>
        clientName === 'mersenne'
          ? `fund${row.original.index}`
          : row.original.internalFundName,
    },
    fundName: {
      accessorKey: 'fundName',
      header: 'Fund Name',
      meta: { align: 'left', minWidth: '10rem', pdfRenderType: 'DetailLine' },
      cell: ({ row }) => (
        <DetailLine
          initialText={row.original.fundName}
          detailLine={row.original.derogationMessage}
          detailStyle={{ margin: 0, color: mainColors.Fail }}
        />
      ),
    },
    reports: {
      accessorKey: 'reports',
      header: 'Reports',
      enableSorting: false,
      meta: { align: 'center', minWidth: '7rem' },
      cell: ({ row }) => (
        <GeneralReportDatePicker
          reportNames={row.original.reports}
          reportUrl="ucits_historical_report"
          fundId={row.original.internalFundName}
        />
      ),
    },
    subFundRiskReports: {
      accessorKey: 'subFundRiskReports',
      header: 'Sub Fund Risk Reports',
      enableSorting: false,
      meta: { align: 'center', minWidth: '8rem' },
      cell: ({ row }) => (
        <GeneralReportDatePicker
          reportNames={row.original.subFundRiskReports}
          reportUrl="ucits_historical_report"
          fundId={row.original.internalFundName}
          customGetDatesFromReportNamesFunction={
            getDatesFromSubFundRiskReportNames
          }
          customGetDatesFromReportNamesAsStringsFunction={
            getDatesFromSubFundRiskReportNamesAsStrings
          }
          customFindReportnameFromDate={findSubFundReportnameFromDate}
        />
      ),
    },
    positionDate: {
      accessorKey: 'positionDate',
      header: 'Position Date',
      meta: { align: 'center', minWidth: '7rem' },
      sortingFn: (a, b) => {
        const aDate = new Date(a.original.positionDate).getTime();
        const bDate = new Date(b.original.positionDate).getTime();
        return aDate - bDate;
      },
      cell: ({ row }) => (
        <CalendarPopup
          date={row.original.positionDate}
          fundId={row.original.internalFundName}
        />
      ),
    },
    calculationDate: {
      accessorKey: 'calculationDate',
      header: 'Latest Calculation Timestamp',
      meta: { align: 'center', minWidth: '6.5rem' },
    },
    currency: {
      accessorKey: 'currency',
      header: 'Currency',
      meta: { align: 'center', width: '6.5rem' },
    },
    netAssetValue: {
      accessorKey: 'netAssetValue',
      header: 'Net Asset Value',
      meta: { align: 'right', minWidth: '8rem' },
      sortingFn: (a, b) =>
        b.original.netAssetValue - a.original.netAssetValue,
      cell: ({ row }) => addCommasToNumbersAndRound(row.original.netAssetValue),
    },
    cssfRiskSpreading: {
      accessorKey: 'cssfRiskSpreading',
      header: 'CSSF Risk Spreading',
      meta: {
        align: 'center',
        minWidth: STATUS_COLUMN_MIN_WIDTH,
        pdfRenderType: 'PageLinkButton',
        pdfWidth: 10,
      },
      sortingFn: (a, b) =>
        statusSort(a.original, b.original, 'cssfRiskSpreading'),
      cell: ({ row }) => (
        <ReportsLinkButton
          section={section}
          page="cssf-risk-spreading"
          status={specifyStatus(row.original.cssfRiskSpreading || '')}
          fundName={row.original.internalFundName}
        />
      ),
    },
    leverage: {
      accessorKey: 'leverage',
      header: 'Leverage',
      meta: {
        align: 'center',
        minWidth: STATUS_COLUMN_MIN_WIDTH,
        pdfRenderType: 'PageLinkButton',
        pdfWidth: 10,
      },
      sortingFn: (a, b) =>
        statusSort(
          a.original,
          b.original,
          'commitmentStatus',
          'commitmentValue',
        ),
      cell: ({ row }) => (
        <PageLinkButton
          status={row.original.leverage}
          fundName={row.original.internalFundName}
          text={row.original.leverage}
          page="exposure"
          section={section}
        />
      ),
    },
    commitmentValue: {
      id: 'commitmentValue',
      accessorFn: (row) =>
        row.fundType === 'commitment' || row.fundType === 'both'
          ? percentageToTwoDecimalPlaces(row.commitmentValue)
          : 'N/R',
      header: 'Commitment',
      meta: {
        align: 'center',
        minWidth: STATUS_COLUMN_MIN_WIDTH,
        pdfRenderType: 'PageLinkButton',
        pdfWidth: 10,
      },
      sortingFn: (a, b) =>
        statusSort(
          a.original,
          b.original,
          'commitmentStatus',
          'commitmentValue',
        ),
      cell: ({ row, getValue }) => (
        <PageLinkButton
          status={row.original.commitmentStatus}
          fundName={row.original.internalFundName}
          text={getValue() as string}
          page="exposure"
          section={section}
        />
      ),
    },
    valueAtRiskValue: {
      id: 'valueAtRiskValue',
      accessorFn: (row) =>
        row.fundType === 'var' || row.fundType === 'both'
          ? percentageToTwoDecimalPlaces(row.valueAtRiskValue)
          : 'N/R',
      header: 'Value at Risk',
      meta: {
        align: 'center',
        minWidth: STATUS_COLUMN_MIN_WIDTH,
        pdfRenderType: 'PageLinkButton',
        pdfWidth: 10,
      },
      sortingFn: (a, b) =>
        statusSort(
          a.original,
          b.original,
          'valueAtRiskStatus',
          'valueAtRiskValue',
        ),
      cell: ({ row, getValue }) => (
        <PageLinkButton
          status={specifyStatus(row.original.valueAtRiskStatus)}
          fundName={row.original.internalFundName}
          text={getValue() as string}
          page="value-at-risk"
          section={section}
          textColor={
            row.original.varDay === 'yes' ? mainColors.secondaryRed : null
          }
        />
      ),
    },
    ucitsLaw: {
      accessorKey: 'ucitsLaw',
      header: 'UCITS Law',
      meta: {
        align: 'center',
        minWidth: STATUS_COLUMN_MIN_WIDTH,
        pdfRenderType: 'PageLinkButton',
        pdfWidth: 10,
      },
      sortingFn: (a, b) => statusSort(a.original, b.original, 'ucitsLaw'),
      cell: ({ row }) => (
        <PageLinkButton
          status={specifyStatus(row.original.ucitsLaw)}
          fundName={row.original.internalFundName}
          page="ucits-law"
          section="ucits"
        />
      ),
    },
    riaifLaw: {
      accessorKey: 'riaifLaw',
      header: 'RIAIF Law',
      meta: {
        align: 'center',
        minWidth: STATUS_COLUMN_MIN_WIDTH,
        pdfRenderType: 'PageLinkButton',
        pdfWidth: 10,
      },
      sortingFn: (a, b) => statusSort(a.original, b.original, 'riaifLaw'),
      cell: ({ row }) => (
        <PageLinkButton
          status={specifyStatus(row.original.riaifLaw || '')}
          fundName={row.original.internalFundName}
          page="riaif-law"
          section={section}
        />
      ),
    },
    qiaifLaw: {
      accessorKey: 'qiaifLaw',
      header: 'QIAIF Law',
      meta: {
        align: 'center',
        minWidth: STATUS_COLUMN_MIN_WIDTH,
        pdfRenderType: 'PageLinkButton',
        pdfWidth: 10,
      },
      sortingFn: (a, b) => statusSort(a.original, b.original, 'qiaifLaw'),
      cell: ({ row }) => (
        <PageLinkButton
          status={specifyStatus(row.original.qiaifLaw || '')}
          fundName={row.original.internalFundName}
          page="qiaif-law"
          section={section}
        />
      ),
    },
    nursLaw: {
      accessorKey: 'nursLaw',
      header: 'NURS Law',
      meta: {
        align: 'center',
        minWidth: STATUS_COLUMN_MIN_WIDTH,
        pdfRenderType: 'PageLinkButton',
        pdfWidth: 10,
      },
      sortingFn: (a, b) => statusSort(a.original, b.original, 'nursLaw'),
      cell: ({ row }) => (
        <PageLinkButton
          status={specifyStatus(row.original.nursLaw || '')}
          fundName={row.original.internalFundName}
          page="nurs-law"
          section="nurs"
        />
      ),
    },
    fivgLaw: {
      accessorKey: 'fivgLaw',
      header: 'Fivg Law',
      meta: {
        align: 'center',
        minWidth: STATUS_COLUMN_MIN_WIDTH,
        pdfRenderType: 'PageLinkButton',
        pdfWidth: 10,
      },
      sortingFn: (a, b) => statusSort(a.original, b.original, 'fivgLaw'),
      cell: ({ row }) => (
        <PageLinkButton
          status={specifyStatus(row.original.fivgLaw || '')}
          fundName={row.original.internalFundName}
          page="fivg-law"
          section={section}
        />
      ),
    },
    partiiLaw: {
      accessorKey: 'partiiLaw',
      header: 'Part II Law',
      meta: {
        align: 'center',
        minWidth: STATUS_COLUMN_MIN_WIDTH,
        pdfRenderType: 'PageLinkButton',
        pdfWidth: 10,
      },
      sortingFn: (a, b) => statusSort(a.original, b.original, 'partiiLaw'),
      cell: ({ row }) => (
        <PageLinkButton
          status={specifyStatus(row.original.partiiLaw || '')}
          fundName={row.original.internalFundName}
          page="partii-law"
          section={section}
        />
      ),
    },
    act40Law: {
      accessorKey: 'act40Law',
      header: '40 Act Law',
      meta: {
        align: 'center',
        minWidth: STATUS_COLUMN_MIN_WIDTH,
        pdfRenderType: 'PageLinkButton',
        pdfWidth: 10,
      },
      sortingFn: (a, b) => statusSort(a.original, b.original, 'act40Law'),
      cell: ({ row }) => (
        <PageLinkButton
          status={specifyStatus(row.original.act40Law || '')}
          fundName={row.original.internalFundName}
          page="act40-law"
          section="act40"
        />
      ),
    },
    prospectusStatus: {
      accessorKey: 'prospectusStatus',
      header: 'Prospectus Restrictions',
      meta: {
        align: 'center',
        minWidth: STATUS_COLUMN_MIN_WIDTH,
        pdfRenderType: 'PageLinkButton',
        pdfWidth: 10,
      },
      sortingFn: (a, b) =>
        statusSort(a.original, b.original, 'prospectusStatus'),
      cell: ({ row }) => (
        <PageLinkButton
          status={specifyStatus(row.original.prospectusStatus)}
          fundName={row.original.internalFundName}
          page="prospectus-restrictions"
          section={section}
        />
      ),
    },
    supplementaryRules: {
      accessorKey: 'supplementaryRules',
      header: 'Supplementary rules',
      meta: {
        align: 'center',
        minWidth: STATUS_COLUMN_MIN_WIDTH,
        pdfRenderType: 'PageLinkButton',
        pdfWidth: 10,
      },
      sortingFn: (a, b) =>
        statusSort(a.original, b.original, 'supplementaryRules'),
      cell: ({ row }) => (
        <PageLinkButton
          status={row.original.supplementaryRules ?? Status.NA}
          fundName={row.original.internalFundName}
          page="supplementary-rules"
          section={section}
        />
      ),
    },
    notes: {
      accessorKey: 'notes',
      header: 'Notes',
      enableSorting: false,
      meta: { align: 'center', width: '4rem' },
      cell: ({ row }) => (
        <NotesComp
          fundId={row.original.internalFundName}
          fundName={row.original.fundName}
          includeSection={true}
          subject={`${section}_${section}-overview`}
          topic={row.original.positionDate}
          positionDate={row.original.positionDate}
          preFetchedNotesLoading={preFetchedNotesLoading}
          preFetchedNotes={
            getNotesForFund
              ? getNotesForFund(
                row.original.internalFundName,
                row.original.positionDate,
              )
              : undefined
          }
        />
      ),
    },
  };

  // Hidden but searchable — matches V1 Material Table
  // `{ hidden: true, field: 'derogationMessage', searchable: true }`.
  // Derogation text is shown under fund name via DetailLine, not as its own column.
  // The accessor coalesces to '' because TanStack's default getColumnCanGlobalFilter
  // samples the first row and only keeps string / number columns.
  const derogationMessageColumn: OverviewColumnDef = {
    id: 'derogationMessage',
    accessorFn: (row) => row.derogationMessage ?? '',
    header: 'Derogation Message',
    enableSorting: false,
    enableGlobalFilter: true,
  };

  const selectedColumns = fields.map((field) => {
    if (allColumns[field]) return allColumns[field];

    return {
      accessorKey: field,
      header: field,
      meta: { align: 'center' as const },
      cell: ({ row }: { row: { original: GeneralOverviewTableData } }) => {
        const value = (row.original as unknown as Record<string, unknown>)[
          field
        ];
        if (value !== null && value !== undefined && isPrimitive(value)) {
          return value as React.ReactNode;
        }
        return null;
      },
    } satisfies OverviewColumnDef;
  });

  return [expandColumn, ...selectedColumns, derogationMessageColumn];
}
